import { useMemo, useState } from "react";
import { useApi } from "@/lib/useApi";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Radio, Share2, Lock, BadgeCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang, useAuth } from "@/context/AppContext";
import { useLiveEvent } from "@/context/RealtimeContext";
import { PrimaryBtn, GhostBtn } from "@/pages/app/Dashboard";
import { FeedCard } from "@/pages/app/Feed";
import { VideoDialog } from "@/components/VideoDialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BroadcastLive } from "@/components/BroadcastLive";
import { CommunityRanking } from "@/components/CommunityRanking";
import { Replays } from "@/components/Replays";

const BroadcastStarter = ({ c, onStart }) => {
  const { t } = useLang();
  const [title, setTitle] = useState("");
  if (!c.can_broadcast) {
    const locked = t("broadcast_locked").replace("{min}", c.min_followers.toLocaleString()).replace("{n}", c.my_followers);
    return <p className="mt-4 flex items-center gap-2 text-xs text-amber-300" data-testid="broadcast-locked"><Lock className="h-3.5 w-3.5" />{locked}</p>;
  }
  const submit = async () => { if (await onStart(title)) setTitle(""); };
  return (
    <div className="mt-4 flex gap-2">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("broadcast_title")} data-testid="broadcast-title-input" />
      <PrimaryBtn disabled={!title.trim()} onClick={submit} data-testid="start-broadcast-btn"><Radio className="h-4 w-4" />{t("start_broadcast")}</PrimaryBtn>
    </div>
  );
};

const Broadcasts = ({ c, reload }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [open, setOpen] = useState(null);
  const openLive = useMemo(() => c.broadcasts.find((b) => b.id === open), [c.broadcasts, open]);
  const join = async (b) => { try { const { data } = await api.post(`/broadcasts/${b.id}/join`); setSession({ ...data, title: b.title }); } catch (e) { toast.error(errMsg(e)); } };
  const start = async (title) => {
    try { const { data } = await api.post(`/communities/${c.id}/broadcasts`, { title }); reload(); join(data); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const end = async (b) => { try { await api.post(`/broadcasts/${b.id}/end`); setSession(null); setOpen(null); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="rounded-md border border-white/[0.08] bg-[#111827] p-6" data-testid="broadcasts-panel">
      <p className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-rose-300"><Radio className="h-4 w-4" />{t("live_now")}</p>
      <div className="space-y-2">
        {c.broadcasts.map((b) => (
          <div key={b.id} className="flex items-center justify-between rounded border border-rose-400/20 px-3 py-2" data-testid={`broadcast-item-${b.id}`}>
            <div><p className="text-sm text-white">{b.title}</p><p className="font-mono text-[10px] text-slate-500">{t("host")}: {b.host_name}</p></div>
            <div className="flex gap-2">
              <GhostBtn onClick={() => setOpen(open === b.id ? null : b.id)} data-testid={`broadcast-open-${b.id}`}>{open === b.id ? t("close_live") : t("open_live")}</GhostBtn>
              <PrimaryBtn onClick={() => join(b)} data-testid={`broadcast-join-${b.id}`}>{b.host_id === user.id ? t("go_live") : t("watch")}</PrimaryBtn>
              {(b.host_id === user.id || user.role === "admin") && <GhostBtn onClick={() => end(b)} data-testid={`broadcast-end-${b.id}`}>{t("end_broadcast")}</GhostBtn>}
            </div>
          </div>
        ))}
        {!c.broadcasts.length && <p className="text-sm text-slate-500">{t("empty")}</p>}
      </div>
      {openLive && <BroadcastLive key={openLive.id} b={openLive} />}
      {c.joined && <BroadcastStarter c={c} onStart={start} />}
      <VideoDialog session={session} title={session?.title} onClose={() => setSession(null)} />
    </section>
  );
};

const ShareBox = ({ c, onShared }) => {
  const { t } = useLang();
  const { data: intents } = useApi("/intents", { initial: [] });
  const mine = useMemo(() => intents.filter((i) => i.status === "active"), [intents]);
  const [sel, setSel] = useState("");
  const share = async () => { try { await api.post(`/communities/${c.id}/share`, { intent_id: sel }); setSel(""); onShared(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex flex-wrap gap-2" data-testid="share-box">
      <Select value={sel} onValueChange={setSel}>
        <SelectTrigger className="w-72" data-testid="share-intent-select"><SelectValue placeholder={t("share_intent")} /></SelectTrigger>
        <SelectContent className="bg-[#111827]">{mine.map((i) => <SelectItem key={i.id} value={i.id} data-testid={`share-intent-option-${i.id}`}>{i.title}</SelectItem>)}</SelectContent>
      </Select>
      <PrimaryBtn disabled={!sel} onClick={share} data-testid="share-intent-btn"><Share2 className="h-4 w-4" />{t("share")}</PrimaryBtn>
    </div>
  );
};

const CommunityHeader = ({ c, onToggle }) => {
  const { t } = useLang();
  return (
    <header className="relative overflow-hidden rounded-md border border-white/[0.08] bg-[#111827] p-8 grid-lines">
      <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">{c.sector}</p>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-white sm:text-4xl" data-testid="community-name">{c.name}</h1>
          <p className="mt-2 text-sm text-slate-400">{c.description}</p>
          <p className="mt-3 inline-flex items-center gap-1.5 font-mono text-xs text-slate-400"><Users className="h-3.5 w-3.5" />{c.members} {t("members")}</p>
        </div>
        <PrimaryBtn onClick={onToggle} data-testid="community-detail-join-btn" className={c.joined ? "!bg-transparent border border-emerald-400/40 !text-emerald-300" : ""}>{c.joined ? t("leave") : t("join")}</PrimaryBtn>
      </div>
    </header>
  );
};

const MemberRow = ({ m }) => {
  const { t } = useLang();
  return (
    <li className="border-b border-white/5 pb-3" data-testid={`community-member-${m.user_id}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-white">{m.name}</span>
        <span className="font-display text-lg font-bold text-emerald-300">{m.max_trust}</span>
      </div>
      <p className="font-mono text-[10px] text-slate-500">{m.followers} {t("followers")}</p>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {m.principals.map((p) => (
          <Link key={p.id} to={`/app/profiles/${p.id}`} className="inline-flex items-center gap-1 rounded bg-white/5 px-2 py-0.5 text-[11px] text-slate-300 hover:text-emerald-300">{p.name}{p.verified && <BadgeCheck className="h-3 w-3 text-emerald-400" />}</Link>
        ))}
      </div>
    </li>
  );
};

const MembersList = ({ members }) => {
  const { t } = useLang();
  return (
    <aside className="rounded-md border border-white/[0.08] bg-[#111827] p-6" data-testid="community-members">
      <p className="mb-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">{t("members_title")}</p>
      <ul className="space-y-4">{members.map((m) => <MemberRow key={m.user_id} m={m} />)}</ul>
    </aside>
  );
};

const SharedIntents = ({ c, onLike, onShared }) => {
  const { t } = useLang();
  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{t("shared_intents")}</p>
        {c.joined && <ShareBox c={c} onShared={onShared} />}
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        {c.shared_intents.map((it) => <FeedCard key={it.id} item={it} onLike={onLike} />)}
        {!c.shared_intents.length && <p className="text-sm text-slate-500">{t("empty")}</p>}
      </div>
    </section>
  );
};

export default function CommunityDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { data: c, reload: load } = useApi(`/communities/${id}`);
  useLiveEvent(["community_update", "feed_like"], (m) => (m.kind === "feed_like" || m.data.community_id === id) && load());
  const toggle = async () => { try { await api.post(`/communities/${id}/${c.joined ? "leave" : "join"}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const like = async (iid) => { try { await api.post(`/feed/${iid}/like`); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!c) return <p className="font-mono text-xs text-slate-500">…</p>;

  return (
    <div className="space-y-8" data-testid="community-detail">
      <Link to="/app/communities" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="back-to-communities"><ArrowLeft className="h-4 w-4" />{t("app_communities")}</Link>
      <CommunityHeader c={c} onToggle={toggle} />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Broadcasts c={c} reload={load} />
          <Replays cid={id} joined={c.joined} />
          <SharedIntents c={c} onLike={like} onShared={load} />
        </div>
        <div className="space-y-6">
          <CommunityRanking cid={id} />
          <MembersList members={c.members_list} />
        </div>
      </div>
    </div>
  );
}
